import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { PageScroll } from 'app/components/layout/PageScroll'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { INBOX_TAB } from 'app/components/contacts/ContactRelatedObjects'
import { Search } from 'app/components/Search'
import LocationFilter from 'app/components/table/filters/LocationFilter'
import { LaneCount } from 'app/components/lists/board/LaneCount'
import { LaneLoadMore } from 'app/components/lists/board/LaneLoadMore'
import { ListBoard } from 'app/components/lists/board/ListBoard'
import { ListBoardHeader } from 'app/components/lists/board/ListBoardHeader'
import { BulkSelectToggle } from 'app/components/lists/board/BulkSelectToggle'
import { useBoardSelection } from 'app/components/lists/board/useBoardSelection'
import type { ListBoardLaneDef } from 'app/components/lists/board/boardTypes'
import { notifySuccess } from 'app/services/notify.client'
import { ContactJson } from 'app/types/entities'
import { useHandleSearchParams, useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { observer } from 'mobx-react-lite'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { BandPicker } from './BandPicker'
import { BoardCard } from './BoardCard'
import { ContactBoardBulkBar } from './ContactBoardBulkBar'
import { SegmentedControl } from './SegmentedControl'
import { BoardColumn, needsClosedLostReason } from './boardColumnSource'
import { showClosedLostQuickModal } from './ClosedLostQuickForm'
import { createClosedLostMilestone } from './closedLostMilestone'
import { ExitDropZone } from './ExitDropZone'
import { EXIT_STAGE_MODAL_OPTIONS, ExitStageListBody, useExitStageContacts } from './ExitStage'
import { closeModal, showModal } from 'app/lib/modal'
import {
  HYGIENE_BAND_OPTIONS,
  HygieneBandId,
  HygieneSortId,
  hygieneBandFromParamOrDefault,
  hygieneBandSearchTerms,
  hygieneSort,
} from './hygieneBands'
import { HYGIENE_SORT_OPTIONS } from './hygieneSortOptions'
import { queueNavSearch } from './locationParam'
import { getPreferredSnoozeDays } from './momentumStore'
import { ACTION_QUEUE_BY_ID, QueueSearchOpts } from './queueRegistry'
import { QueueViewToggle } from './QueueViewToggle'
import { SCHEDULED_STAGE } from './systemPipelines'
import { useBoardTasks } from './useBoardTasks'
import { useContactBoard } from './useContactBoard'

/**
 * How long after a press on a card the drawer's own outside-click close is
 * read as a switch instead: long enough to cover pointerdown, mouseup and the
 * click behind them; short enough that an abandoned press cannot swallow the
 * next real click.
 */
const CARD_PRESS_GRACE_MS = 500

/**
 * The board any queue with a `board` config can use. The columns and the
 * writes come from that config, so this component never knows whether it is
 * moving a lead status or a pipeline membership.
 */
// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const ContactBoardComponent = observer(function ContactBoardComponent({
  queueId,
  pipelineName,
  headerControls,
  totalItems,
  defaultBand,
}: {
  queueId: string
  /**
   * Header title. The queue's own `def.name` describes only one hygiene slice
   * once the band control widens the board, so the pipeline's name wins;
   * `def.name` stays as the fallback for a caller without a pipeline in hand.
   */
  pipelineName?: string
  /**
   * A list page's view switch, drawn at the right end of the controls row.
   * When given, the page is the board's home: no back link, and the board's own band
   * control and view toggle stay off so the one pair sits where every view of the list
   * puts it.
   */
  headerControls?: ReactNode
  /** The list's count for the title chip; the sidebar row's number. */
  totalItems?: number
  /** The window a bare URL opens on; the board's own default otherwise. */
  defaultBand?: HygieneBandId
}) {
  const routerLocation = useLocation()
  const user = useUser()
  const workspace = useWorkspace()
  const def = ACTION_QUEUE_BY_ID.get(queueId)
  // Gated on `bundle` rather than `queueId` so a future queue joining the
  // hygiene bundle picks the band control up for free.
  const isHygieneBundle = def?.bundle === `hygiene`
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const bandParam = searchParams.get(`band`)
  const band = isHygieneBundle
    ? bandParam === null && defaultBand
      ? defaultBand
      : hygieneBandFromParamOrDefault(bandParam)
    : undefined
  // "All" writes `band=all` rather than dropping the param: an absent param is
  // what the default reads as, so stripping it would bounce the board back to
  // the default window on the next read.
  const setBand = (id: HygieneBandId | undefined) => handleSearchParams({ band: id ?? `all` })
  // Lets the band control put any hygiene window, or their union, on the one
  // `due-for-hygiene` queue without a second `ActionQueueDef` per band.
  const searchTermsOverride = useCallback((opts: QueueSearchOpts) => hygieneBandSearchTerms(band, opts), [band])
  // Each window brings its own order, and a picked one sits on top of it for
  // as long as you stay in that window. Without any of this every band sorted
  // by the bundle default, so "Due within 30 days" opened on the people due in
  // 29 days.
  const [chosenSort, setChosenSort] = useState<HygieneSortId | undefined>()
  // Dropped on every window change: each window's declared order is the one
  // worth arriving on, and an order picked in one window is routinely the
  // wrong way round in the next.
  useEffect(() => setChosenSort(undefined), [band])
  const needsActionDateEnabled = !!useFeatureToggle(`needs-action-date`)
  // The same opts `useContactBoard` builds for the callback below, needed out
  // here so the control can show which option is live before any click.
  const sortOpts = useMemo(() => ({ needsActionDateEnabled }), [needsActionDateEnabled])
  const order = isHygieneBundle && def ? hygieneSort(band, sortOpts, def, chosenSort) : undefined
  const sortOverride = useCallback(
    (opts: QueueSearchOpts) => (def ? hygieneSort(band, opts, def, chosenSort).spec : undefined),
    [band, chosenSort, def],
  )
  const board = useContactBoard(
    def,
    { userId: user?.id, workspaceId: workspace?.id },
    isHygieneBundle ? searchTermsOverride : undefined,
    isHygieneBundle ? sortOverride : undefined,
  )
  const cardIds = useMemo(
    () => board.lanes.flatMap((lane) => lane.contacts.map((contact) => contact.id).filter(Boolean) as string[]),
    [board.lanes],
  )
  const visibleIds = useMemo(() => new Set(cardIds), [cardIds])
  const bulk = useBoardSelection(visibleIds)
  const bulkSelection = useMemo(
    () => ({
      rows: board.lanes.flatMap((lane) =>
        lane.contacts.filter((contact) => contact.id && bulk.selected.has(contact.id)),
      ),
      clear: bulk.clear,
    }),
    [board.lanes, bulk.selected, bulk.clear],
  )
  // Bumped whenever the drawer changes something a card draws: the board's own
  // inputs do not change (same contacts, same task query key), so the drawer
  // has to say when to re-read.
  const [drawerNonce, setDrawerNonce] = useState(0)
  const noteDrawerChange = useCallback(() => {
    setDrawerNonce((previous) => previous + 1)
    board.refresh()
  }, [board])
  const tasks = useBoardTasks(cardIds, drawerNonce)
  const exitPipelineId = def?.board?.source === `pipeline` ? def.board.pipelineId : undefined
  const [exitList, setExitList] = useState<BoardColumn | undefined>(undefined)
  const exitContacts = useExitStageContacts(exitPipelineId, exitList?.key ?? ``, !!exitList)
  const exitIds = useMemo(() => (exitContacts ?? []).map((c) => c.id ?? ``).filter(Boolean), [exitContacts])
  const exitTasks = useBoardTasks(exitIds)

  // The exit list rides the app's modal system, so it is re-shown (not
  // nested) whenever its late-arriving data lands. Closing it from the modal
  // chrome clears the state; clearing the state closes the modal.
  useEffect(() => {
    if (!exitList || !def) {
      closeModal()
      return
    }
    showModal(
      <ExitStageListBody
        count={exitContacts?.length ?? 0}
        loading={exitContacts === undefined}
        empty={exitContacts?.length === 0}
      >
        {(exitContacts ?? []).map((contact) => (
          <BoardCard
            key={contact.id}
            def={def}
            contact={contact}
            dragging={false}
            // Nothing to drag to in here: the lanes are not on screen.
            onDragStart={() => {}}
            onDragEnd={() => {}}
            onOpen={() => {
              setExitList(undefined)
              setDrawerTab(undefined)
              setDrawerContact(contact)
            }}
            onText={() => {
              setExitList(undefined)
              setDrawerTab(INBOX_TAB)
              setDrawerContact(contact)
            }}
            onSnooze={() => {}}
            onPress={() => {}}
            tasks={contact.id ? exitTasks.get(contact.id) : undefined}
            workspaceId={workspace?.id}
          />
        ))}
      </ExitStageListBody>,
      {
        ...EXIT_STAGE_MODAL_OPTIONS,
        title: `${exitList.label} · ${exitContacts?.length ?? 0}`,
        onClose: () => setExitList(undefined),
      },
      false,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-show on the data the body renders
  }, [exitList, exitContacts, exitTasks])
  // A move into Closed Lost gates on a reason: the modal opens instead of
  // writing, and the card stays put until it confirms or is cancelled.
  const [drawerContact, setDrawerContact] = useState<ContactJson | undefined>(undefined)
  /** Which tab the drawer opens on, when the card said where it was going. */
  const [drawerTab, setDrawerTab] = useState<string | undefined>(undefined)
  /**
   * When a press last landed on a card. The drawer closes itself on any
   * pointer press outside it, and a press on another card is outside it; the
   * card's own `onPointerDown` runs first, so a close arriving right behind
   * one is a switch, not a close. A timestamp rather than a flag because a
   * press that never becomes a click has to expire on its own.
   */
  const pressedCardAt = useRef(0)
  const closeDrawer = () => {
    if (Date.now() - pressedCardAt.current < CARD_PRESS_GRACE_MS) return
    setDrawerContact(undefined)
    setDrawerTab(undefined)
    board.refresh()
  }

  const boardSource = def?.board?.source

  // Drops only ever carry an id (see BoardCard's onDragStart), and the Closed
  // Lost gate needs the full record to hand to the modal.
  const findContact = (contactId: string): ContactJson | undefined => {
    for (const lane of board.lanes) {
      const found = lane.contacts.find((c) => c.id === contactId)
      if (found) return found
    }
    return undefined
  }

  const handleDrop = (contactId: string, columnKey: string) => {
    if (boardSource && needsClosedLostReason(boardSource, columnKey)) {
      const contact = findContact(contactId)
      if (contact && user)
        showClosedLostQuickModal({
          contact,
          user,
          onSubmit: (data) =>
            // The milestone write replaces board.move's own leadStatus write:
            // the backend derives leadStatus off the milestone, so writing it
            // separately would race the field.
            bg(
              board.move(contact.id!, `Closed Lost`, {
                write: () => createClosedLostMilestone(contact.id!, user.orgId, data),
              }),
            ),
          onCancel: () => {},
        })
      return
    }
    // Announce a booking only once the write has landed; a failed move has
    // already put the card back and said why.
    const name = findContact(contactId)?.name
    bg(
      board.move(contactId, columnKey).then(({ ok }) => {
        if (ok && columnKey === SCHEDULED_STAGE) {
          notifySuccess(`Marked ${name ?? `this contact`} as scheduled`)
        }
      }),
    )
  }

  // Closed Lost on a lead-status board asks for a reason one contact at a time, so it stays a drag.
  const bulkStages = [...board.lanes.map((lane) => lane.column), ...board.exits]
    .filter((column) => !boardSource || !needsClosedLostReason(boardSource, column.key))
    .map((column) => ({ label: column.label, value: column.key }))

  const lanes: ListBoardLaneDef<ContactJson>[] = board.lanes.map((lane) => ({
    key: lane.column.key,
    label: lane.column.label,
    count: <LaneCount shown={lane.contacts.length} total={lane.total} />,
    items: lane.contacts,
    emptyLabel: board.loading ? `Loading` : `Nothing here`,
    footer: lane.hasMore && (
      <LaneLoadMore
        remaining={lane.total - lane.contacts.length}
        loading={board.loadingColumns.has(lane.column.key)}
        onClick={() => board.loadMore(lane.column.key)}
      />
    ),
  }))

  if (!def) return null

  const title = pipelineName ?? def.name
  const bulkToggle = def.board && <BulkSelectToggle selecting={bulk.selecting} onToggle={bulk.toggleSelecting} />

  return (
    // `relative` docks the bulk bar to the page rather than to the scrolling content.
    <PageScroll className="relative" contentClassName="flex h-full flex-col pb-6 pt-8">
      <ListBoardHeader
        backTo={headerControls ? undefined : `/action-center${queueNavSearch(routerLocation.search)}`}
        title={title}
        totalItems={totalItems}
        // The band picker and sort narrow and order the lanes, so they follow Search and
        // locations on the left; a list page's own controls end the row on the right.
        controls={
          <>
            <Search />
            <LocationFilter customSearchParam="filters" />
            {isHygieneBundle && !headerControls && (
              // No `noun`, unlike the treatment board's: these windows name themselves
              // ("Due within 30 days"), so a word in front of them would say nothing.
              <BandPicker
                menuLabel="Cleaning due"
                options={HYGIENE_BAND_OPTIONS.map((option) => ({
                  id: option.id,
                  label: option.label,
                  blurb:
                    (option.id && ACTION_QUEUE_BY_ID.get(option.id)?.definition) ??
                    `Everyone due a cleaning, whenever it fell due.`,
                }))}
                value={band}
                onSelect={setBand}
              />
            )}
            {order && (
              <SegmentedControl options={HYGIENE_SORT_OPTIONS} value={order.current} onSelect={setChosenSort} />
            )}
          </>
        }
        headerControls={headerControls}
        viewToggle={
          headerControls ? (
            bulkToggle
          ) : (
            <div className="flex items-center gap-2">
              {bulkToggle}
              <QueueViewToggle queueId={def.id} userId={user?.id} current="board" search={routerLocation.search} />
            </div>
          )
        }
        banner={
          (board.error || board.loading) && (
            <p className="mt-4 rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-subtle">
              {board.error ? <span className="text-strong">{board.error}</span> : `Reading the queue.`}
            </p>
          )
        }
      />

      {!def.board ? (
        <p className="mt-8 w-full text-body-sm text-subtle">This queue does not have a board yet.</p>
      ) : (
        <ListBoard
          lanes={lanes}
          itemId={(contact) => contact.id}
          touchDrag
          selection={bulk.selection}
          onMove={handleDrop}
          onExitDrop={handleDrop}
          renderCard={(contact, drag) => (
            <BoardCard
              def={def}
              contact={contact}
              dragging={drag.dragging}
              onDragStart={drag.onDragStart}
              onDragEnd={drag.onDragEnd}
              select={drag.select}
              onOpen={() => {
                setDrawerTab(undefined)
                setDrawerContact(contact)
              }}
              onText={() => {
                setDrawerTab(INBOX_TAB)
                setDrawerContact(contact)
              }}
              onSnooze={() => {
                if (!contact.id) return
                board.snooze(contact.id, getPreferredSnoozeDays())
                notifySuccess(`Snoozed ${contact.name ?? `this contact`} for ${getPreferredSnoozeDays()} days`)
              }}
              onPress={() => (pressedCardAt.current = Date.now())}
              tasks={contact.id ? tasks.get(contact.id) : undefined}
              workspaceId={workspace?.id}
            />
          )}
          renderExits={
            board.exits.length > 0
              ? (drag) =>
                  board.exits.map((exit) => (
                    <ExitDropZone
                      key={exit.key}
                      column={exit}
                      draggingId={drag.draggingId}
                      touchOver={drag.touchOverKey === exit.key}
                      pipelineId={exitPipelineId}
                      writes={board.writes}
                      onDrop={(contactId) => {
                        drag.endDrag()
                        handleDrop(contactId, exit.key)
                      }}
                      onOpenList={() => setExitList(exit)}
                    />
                  ))
              : undefined
          }
        />
      )}

      {bulk.selecting && boardSource && (
        <ContactBoardBulkBar
          selection={bulkSelection}
          stages={bulkStages}
          omitLeadStatus={boardSource === `lead-status`}
          setStage={(contactId, columnKey, known, opts) =>
            board.move(contactId, columnKey, { known, quiet: opts?.quiet })
          }
          onExit={bulk.exit}
        />
      )}

      {/* The board re-reads on close: the drawer can change anything a lane is keyed on,
          and none of that reaches the lists behind it. */}
      <ContactDrawer
        openDrawer={Boolean(drawerContact)}
        contact={drawerContact}
        initialTab={drawerTab}
        onClose={closeDrawer}
        onChange={(next) => {
          setDrawerContact(next)
          noteDrawerChange()
        }}
        onTaskUpdated={noteDrawerChange}
        onActivityAdded={noteDrawerChange}
      />
    </PageScroll>
  )
})
